import { addBreadcrumb, captureException } from '@sentry/react'
import { handleNotAwaitedPromise } from '@valian/react-kitchen-sink'
import { useStore } from 'zustand'
import { createStore } from 'zustand/vanilla'

import { peekPiloteSession, type PiloteSessionStatus, touchPiloteSession } from './piloteSessionApi.ts'

export type SessionPhase = 'active' | 'expired' | 'dialog-open' | 'dismissed'

interface SessionState {
  readonly pinnedSession: string | null
  readonly embedded: boolean
  readonly phase: SessionPhase
}

const MAX_CHECK_INTERVAL_MS = 60_000
const FIRST_RETRY_MS = 10_000
// Floor between two expiry checks, so a session Pilote reports as about to expire never turns the
// timer into a request loop.
const MIN_CHECK_DELAY_MS = 5000

// A third of the session's length leaves two more touches before a short (test-stack) session could
// lapse under continuous activity; production's 180-minute sessions keep the one-minute pace.
const touchIntervalFor = (maxInactiveMillis: number) => Math.min(MAX_CHECK_INTERVAL_MS, maxInactiveMillis / 3)

// The phase drives what React renders; the timers and the touch pacing below stay out of it.
const sessionState = createStore<SessionState>(() => ({ pinnedSession: null, embedded: false, phase: 'active' }))

let expiresAt: number | null = null
let lastTouchAt = 0
let checkIntervalMs = MAX_CHECK_INTERVAL_MS
let expiryTimer: ReturnType<typeof setTimeout> | undefined
let pendingCheck: Promise<boolean> | null = null
let failureReported = false

const phaseNow = () => sessionState.getState().phase

const setPhase = (phase: SessionPhase) => {
  sessionState.setState({ phase })
}

// While Pilote is unreachable every tab retries each minute: one Sentry event per outage is enough.
const reportCheckFailure = (error: unknown) => {
  if (failureReported) {
    addBreadcrumb({ category: 'session', message: 'Pilote session check failed again', level: 'warning' })
    return
  }
  failureReported = true
  captureException(error, { level: 'warning' })
}

const scheduleExpiryCheck = (delay: number) => {
  clearTimeout(expiryTimer)
  expiryTimer = setTimeout(
    () => {
      handleNotAwaitedPromise(checkNow())
    },
    Math.max(delay, MIN_CHECK_DELAY_MS)
  )
}

const applyStatus = (status: PiloteSessionStatus) => {
  failureReported = false
  if (status.status === 'expired') {
    clearTimeout(expiryTimer)
    if (phaseNow() === 'active') {
      setPhase('expired')
    }
    return false
  }
  expiresAt = status.expiresAt
  checkIntervalMs = touchIntervalFor(status.maxInactiveMillis)
  scheduleExpiryCheck(status.expiresAt - Date.now() + 1000)
  return true
}

const isPastExpiry = () => expiresAt !== null && Date.now() >= expiresAt

export const isPinned = () => sessionState.getState().pinnedSession !== null

export const pinnedPiloteSession = () => sessionState.getState().pinnedSession ?? undefined

export const isEmbeddedSession = () => sessionState.getState().embedded

export const sessionPhase = phaseNow

export const useSessionPhase = () => useStore(sessionState, (state) => state.phase)

export const pinSession = (session: string, status: PiloteSessionStatus | null, options: { embedded: boolean }) => {
  expiresAt = null
  lastTouchAt = Date.now()
  checkIntervalMs = MAX_CHECK_INTERVAL_MS
  sessionState.setState({ pinnedSession: session, embedded: options.embedded, phase: 'active' })
  if (status) {
    applyStatus(status)
  } else {
    // Pilote could not be asked at sign-in: ask again soon rather than never learn the expiry, and
    // before a short session could lapse under the default one-minute pace.
    scheduleExpiryCheck(FIRST_RETRY_MS)
  }
}

export const canProceed = () => !isPinned() || (phaseNow() === 'active' && !isPastExpiry())

export function checkNow(): Promise<boolean> {
  const session = pinnedPiloteSession()
  if (!session) {
    return Promise.resolve(true)
  }
  pendingCheck ??= peekPiloteSession(session)
    .then(applyStatus)
    .catch((error: unknown) => {
      reportCheckFailure(error)
      scheduleExpiryCheck(checkIntervalMs)
      return phaseNow() === 'active'
    })
    .finally(() => {
      pendingCheck = null
    })
  return pendingCheck
}

export const openSessionDialog = () => {
  if (isPinned()) {
    setPhase('dialog-open')
  }
}

export const dismissSessionDialog = () => {
  if (phaseNow() === 'dialog-open') {
    setPhase('dismissed')
  }
}

export const markSessionExpired = () => {
  if (!isPinned()) {
    return
  }
  clearTimeout(expiryTimer)
  openSessionDialog()
}

export const confirmSessionThenOpenDialog = async () => {
  if (phaseNow() !== 'active') {
    openSessionDialog()
    return
  }
  if (!(await checkNow())) {
    openSessionDialog()
  }
}

const touch = async (session: string) => {
  try {
    if (!applyStatus(await touchPiloteSession(session))) {
      openSessionDialog()
    }
  } catch (error) {
    reportCheckFailure(error)
  }
}

export const recordActivity = () => {
  const session = pinnedPiloteSession()
  if (!session || phaseNow() !== 'active' || Date.now() - lastTouchAt < checkIntervalMs) {
    return
  }
  lastTouchAt = Date.now()
  handleNotAwaitedPromise(touch(session))
}
